import type {
  AgentThreadToolUseContent,
  ClientToolResult,
} from "../AgentThreadRecord";

import {
  type SavedToolPermissions,
  toolCallNeedsApproval,
} from "./approvalModes";
import {
  type ClientTool,
  type ClientToolBatchScope,
  type ClientToolDeps,
  ToolErrorKind,
  classifyToolFailure,
  describeToolFailure,
  toolError,
} from "./ClientTools";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PendingToolApproval {
  toolUse: AgentThreadToolUseContent;
  tool: ClientTool;
}

export interface BatchRunnerCallbacks {
  /** Called whenever the current pending approval changes (including to null). */
  onPendingApprovalChanged: (pending: PendingToolApproval | null) => void;

  /** Called when all tools in the batch have resolved and results are ready to submit. */
  onBatchComplete: (threadId: string, results: ClientToolResult[]) => void;
}

export interface BatchRunConfig {
  threadId: string;
  toolUses: AgentThreadToolUseContent[];
  autoEnabled: boolean;
  savedPermissions: SavedToolPermissions;
  deps: ClientToolDeps;
  /**
   * Returns the tool available to the person that a call names, or undefined
   * when none has that name. Right before each call runs, the runner resolves
   * it again with the `resolveClientTool` most recently passed to
   * `startBatch` or `updateExecutionSettings`, so a call whose tool stopped
   * being available is refused instead of run.
   */
  resolveClientTool: (toolName: string) => ClientTool | undefined;
}

/**
 * The settings a batch's calls run under. The latest settings apply to every
 * call that has not started.
 */
type ExecutionSettings = Pick<
  BatchRunConfig,
  "autoEnabled" | "savedPermissions" | "deps" | "resolveClientTool"
>;

// ---------------------------------------------------------------------------
// Result helpers
// ---------------------------------------------------------------------------

function buildToolResult(
  toolUse: AgentThreadToolUseContent,
  result: { response: Record<string, unknown>; success: boolean },
  runtimeMs: number,
): ClientToolResult {
  return {
    tool_name: toolUse.tool_name,
    tool_use_id: toolUse.tool_use_id,
    runtime_ms: runtimeMs,
    status: result.success ? "success" : "error",
    output: result.response,
  };
}

const TOOL_NOT_AVAILABLE_MESSAGE = "This tool is not available to you.";

function buildNotAvailableResult(
  toolUse: AgentThreadToolUseContent,
): ClientToolResult {
  return buildToolResult(
    toolUse,
    // Not transient: the same call is refused the same way.
    toolError(TOOL_NOT_AVAILABLE_MESSAGE, {}, ToolErrorKind.VALIDATION),
    0,
  );
}

function buildDeclinedResult(
  toolUse: AgentThreadToolUseContent,
): ClientToolResult {
  return {
    tool_name: toolUse.tool_name,
    tool_use_id: toolUse.tool_use_id,
    runtime_ms: 0,
    status: "error",
    output: { message: "User declined to execute this tool." },
  };
}

/**
 * Result for a tool whose approval prompt went off screen before anyone could
 * answer it.
 *
 * Phrased so the model treats it as an interruption rather than a refusal: the
 * tool never ran and nothing about the request was wrong, so re-requesting is
 * the right move. A declined result would teach it the opposite.
 */
function buildInterruptedResult(
  toolUse: AgentThreadToolUseContent,
): ClientToolResult {
  return {
    tool_name: toolUse.tool_name,
    tool_use_id: toolUse.tool_use_id,
    runtime_ms: 0,
    status: "error",
    output: {
      message:
        "Navigation closed this conversation view before the user could " +
        "approve this tool, so it did not run. Request it again if it is " +
        "still needed.",
    },
  };
}

// ---------------------------------------------------------------------------
// Runner
// ---------------------------------------------------------------------------

/**
 * Pure state machine for batch client-tool execution with approval gating.
 *
 * Manages the triage (auto-execute vs queue for approval), approval queue,
 * result accumulation, and "batch complete" detection. No React, no hooks,
 * no DOM — all interaction with the component tree happens via callbacks.
 *
 * This is the only path from a `tool_use` to a tool's `execute`, which is what
 * makes the approval gate unavoidable rather than a convention the UI upholds:
 * a REQUIRE_APPROVAL tool cannot run without an `approve()` call, and it runs
 * at most once per tool_use_id however many times a batch is re-entered.
 *
 * The hook `useClientToolExecution` creates a runner and acts as a thin
 * adapter that bridges React context (services, navigation, state) to
 * the runner's lifecycle methods.
 */
export class ClientToolBatchRunner {
  private pendingQueue: PendingToolApproval[] = [];
  private executionSettings: ExecutionSettings | null = null;
  private reevaluatingApprovals = false;
  private accumulatedResults: ClientToolResult[] = [];
  private claimedToolCount = 0;
  private batchThreadId: string | null = null;
  private currentApproval: PendingToolApproval | null = null;
  /**
   * Every tool_use_id this runner has taken responsibility for, across batches.
   *
   * Deliberately outlives {@link reset}. The effect that drives the runner
   * re-fires on any change to the thread record — including the `roboto`
   * message the server appends mid-turn with partial server-tool results — and
   * those re-entries carry the same tool_use_ids. This set is what makes them
   * no-ops instead of a second `client_create_collection`.
   */
  private handledToolUseIds = new Set<string>();
  /**
   * What the tools of the current batch have told each other.
   *
   * The runner owns it because it is the only thing that knows where one batch
   * ends: the hook rebuilds its deps every render (so state kept there is
   * either stale or shared with React), and a module-level object would carry
   * a navigation from one batch into the next. Replaced wholesale by
   * {@link reset}, so a tool can never read a flag set turns ago.
   */
  private batchScope: ClientToolBatchScope = {};
  /**
   * Set once this runner's conversation view is gone and can no longer host an approval.
   *
   * Consulted during triage as well as by {@link detach}, because the unmount
   * can land while an auto-executed tool is still awaited, typically the
   * navigating tool that caused it. Triage reaches the gated tools after that
   * await, and without this flag would queue them onto a runner nobody is
   * listening to.
   */
  private detached = false;
  private callbacks: BatchRunnerCallbacks;

  constructor(callbacks: BatchRunnerCallbacks) {
    this.callbacks = callbacks;
  }

  /**
   * Take in a batch of tool uses and answer, run, or queue each one:
   * 1. A call whose tool is not available to the person gets a refusal.
   * 2. A call its tool refuses before approval gets that refusal.
   * 3. A call that needs no approval runs.
   * 4. A call that needs approval is queued.
   *
   * Fires `onPendingApprovalChanged` when a tool needs approval, or
   * `onBatchComplete` once every claimed tool_use has a result.
   *
   * Re-entrant by tool_use_id, not by call: ids already handled are dropped, so
   * calling this again with a batch that is still in flight neither re-runs a
   * write nor discards results already collected. Ids not seen before join the
   * batch in flight — a queued approval stays on screen and the newcomers line
   * up behind it.
   */
  async startBatch(config: BatchRunConfig): Promise<void> {
    if (config.threadId !== this.batchThreadId || !this.hasUnsettledWork()) {
      this.reset();
    }
    this.batchThreadId = config.threadId;
    this.executionSettings = config;

    const fresh = config.toolUses.filter(
      (toolUse) => !this.handledToolUseIds.has(toolUse.tool_use_id),
    );
    if (fresh.length === 0) {
      return;
    }
    // Claim the whole set before the first await. `executeAndRecord` yields,
    // and a re-entry landing in that window must not pick up a tool_use this
    // call is already running.
    for (const toolUse of fresh) {
      this.handledToolUseIds.add(toolUse.tool_use_id);
    }
    this.claimedToolCount += fresh.length;

    for (const toolUse of fresh) {
      const tool = config.resolveClientTool(toolUse.tool_name);

      if (!tool) {
        this.accumulatedResults.push(buildNotAvailableResult(toolUse));
        continue;
      }

      const settings = this.executionSettings;

      // A call the tool refuses from its input and deps gets that refusal as
      // its result without an approval prompt, since approving it would only
      // lead to the same refusal.
      const refusal = tool.refusalBeforeApproval?.(
        toolUse.input ?? {},
        settings.deps,
      );
      if (refusal !== undefined) {
        this.accumulatedResults.push(buildToolResult(toolUse, refusal, 0));
        continue;
      }

      if (
        toolCallNeedsApproval(
          tool,
          settings.savedPermissions,
          settings.autoEnabled,
        )
      ) {
        if (this.detached) {
          this.accumulatedResults.push(buildInterruptedResult(toolUse));
          continue;
        }
        this.pendingQueue.push({ toolUse, tool });
      } else {
        await this.executeAndRecord(toolUse, this.scoped(settings.deps));
      }
    }

    await this.runWaitingCallsSettingsAllow();
  }

  /**
   * Apply the person's automatic-run switch, saved tool permissions, and
   * available tools, including to calls already waiting for approval. This
   * runs each waiting call the new settings no longer gate, with the deps
   * `settings` carries, and refuses each waiting call whose tool is no longer
   * available. A call already running finishes under the settings it started
   * with.
   */
  async updateExecutionSettings(settings: ExecutionSettings): Promise<void> {
    this.executionSettings = settings;
    await this.runWaitingCallsSettingsAllow();
  }

  /**
   * The person approved the call on screen. Runs it with `deps`, which the
   * hook reads at click time so they reflect the page as it is then, and then
   * shows the next waiting call or completes the batch.
   */
  async approve(deps: ClientToolDeps): Promise<void> {
    if (!this.currentApproval) {
      return;
    }

    const { toolUse } = this.currentApproval;
    // Claim the approval *before* awaiting. `executeAndRecord` is async, so a
    // second call arriving while it is in flight would otherwise find
    // `currentApproval` still set and run the same tool_use again, creating
    // two collections and discarding the second result once the batch
    // completes. The UI also latches its Approve button; this is the half that
    // holds regardless of which conversation view calls in.
    this.currentApproval = null;
    // The caller's deps are fresh, but they carry no batch scope of their own:
    // an approval-gated tool sitting between two auto-executed ones has to see
    // the same scope they did.
    await this.executeAndRecord(toolUse, this.scoped(deps));
    // A pass over the waiting calls during the await may already have put the
    // next one on screen.
    if (!this.currentApproval) {
      this.advanceQueue();
    }
  }

  /**
   * User declined the currently pending tool. Records a declined result
   * and advances the queue.
   */
  decline(): void {
    if (!this.currentApproval) {
      return;
    }

    this.accumulatedResults.push(
      buildDeclinedResult(this.currentApproval.toolUse),
    );
    this.advanceQueue();
  }

  /**
   * The conversation view that shows this runner's approvals is going away;
   * answer whatever it still owes.
   *
   * A tool that navigates takes its own conversation view off screen, and the
   * batch's tool_use_ids are claimed process-wide for the life of the tab, so
   * the view that inherits the thread cannot pick the work up. Anything left
   * queued here would never be answered: no approval prompt in any view, and
   * no results submitted, not even those of the tools that did run, because
   * the batch never reaches its claimed count. The turn stops there.
   *
   * One runner has to answer every id of a batch: the server resumes
   * generation on any submission, so a batch answered in two pieces fails
   * provider validation on the unanswered ids. This runner holds the claim, so
   * it answers each waiting call as interrupted.
   *
   * The hook calls this when the component using it unmounts. It must not
   * call it for the cleanup React StrictMode runs at mount in development:
   * the runner cannot tell that cleanup from an unmount, and nothing clears
   * the flag this sets. A runner that owes nothing, because it is idle or its
   * batch already completed, submits nothing. A batch that reaches triage
   * after this call (an awaited auto tool that navigated) still gets its gated
   * tools answered as interrupted.
   */
  detach(): void {
    this.detached = true;

    const stranded = [
      ...(this.currentApproval ? [this.currentApproval] : []),
      ...this.pendingQueue,
    ];
    this.currentApproval = null;
    this.pendingQueue = [];
    for (const { toolUse } of stranded) {
      this.accumulatedResults.push(buildInterruptedResult(toolUse));
    }
    this.completeBatchIfAnswered();
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  /**
   * Drop the current batch's state, and take its approval off the screen when
   * one is showing: the runner answers neither Approve nor Decline for a call
   * of a batch it dropped. {@link handledToolUseIds} is kept, for the reason its
   * docstring gives.
   */
  private reset(): void {
    const approvalOnScreen = this.currentApproval !== null;
    this.pendingQueue = [];
    this.accumulatedResults = [];
    this.claimedToolCount = 0;
    this.batchThreadId = null;
    this.currentApproval = null;
    this.batchScope = {};
    if (approvalOnScreen) {
      this.callbacks.onPendingApprovalChanged(null);
    }
  }

  /** The caller's deps, with this batch's shared scope attached. */
  private scoped(deps: ClientToolDeps): ClientToolDeps {
    return { ...deps, batchScope: this.batchScope };
  }

  /**
   * Run each waiting call that the latest settings no longer gate and refuse
   * each one whose tool is no longer available; then show the next waiting
   * call or complete the batch.
   *
   * One pass runs at a time. Invoked while a pass is running, this returns at
   * once: the running pass rereads the settings and the waiting calls before
   * each pick, so it covers whatever changed.
   */
  private async runWaitingCallsSettingsAllow(): Promise<void> {
    if (this.detached) {
      // detach() answered every waiting call; a batch that triaged after it
      // still owes its submission.
      this.completeBatchIfAnswered();
      return;
    }
    if (this.reevaluatingApprovals || !this.executionSettings) {
      return;
    }
    this.reevaluatingApprovals = true;
    try {
      while (!this.detached) {
        const latestSettings = this.executionSettings;
        const allowed = [
          ...(this.currentApproval ? [this.currentApproval] : []),
          ...this.pendingQueue,
        ].find(
          // A call whose tool is no longer available is taken too:
          // executeAndRecord answers it with a refusal.
          ({ toolUse, tool }) =>
            latestSettings.resolveClientTool(toolUse.tool_name) === undefined ||
            !toolCallNeedsApproval(
              tool,
              latestSettings.savedPermissions,
              latestSettings.autoEnabled,
            ),
        );
        if (!allowed) {
          break;
        }
        // Take the call off the screen or the queue before running it, so an
        // approval click or another settings change cannot run it a second
        // time.
        if (allowed === this.currentApproval) {
          this.currentApproval = null;
          this.callbacks.onPendingApprovalChanged(null);
        } else {
          this.pendingQueue.splice(this.pendingQueue.indexOf(allowed), 1);
        }
        await this.executeAndRecord(
          allowed.toolUse,
          this.scoped(latestSettings.deps),
        );
      }
    } finally {
      this.reevaluatingApprovals = false;
      if (
        !this.currentApproval &&
        !this.detached &&
        this.pendingQueue.length > 0
      ) {
        this.advanceQueue();
      } else {
        this.completeBatchIfAnswered();
      }
    }
  }

  /** True while any claimed tool_use is still queued, on screen, or running. */
  private hasUnsettledWork(): boolean {
    return (
      this.currentApproval !== null ||
      this.pendingQueue.length > 0 ||
      this.accumulatedResults.length < this.claimedToolCount
    );
  }

  /**
   * Run `toolUse` with the tool the latest settings resolve for it and record
   * the result. A call whose tool is no longer available gets a refusal.
   */
  private async executeAndRecord(
    toolUse: AgentThreadToolUseContent,
    deps: ClientToolDeps,
  ): Promise<void> {
    const tool = this.executionSettings?.resolveClientTool(toolUse.tool_name);
    if (!tool) {
      this.accumulatedResults.push(buildNotAvailableResult(toolUse));
      return;
    }
    const inputParams = toolUse.input ?? {};
    try {
      const startTime = performance.now();
      const result = await tool.execute(inputParams, deps);
      const runtimeMs = Math.round(performance.now() - startTime);
      this.accumulatedResults.push(buildToolResult(toolUse, result, runtimeMs));
    } catch (error) {
      // Classify here too, not just inside the tools: a throw that escapes
      // `execute` reaches the model as a tool result like any other, and an
      // unclassified one reads as retryable — the wrong guess for a write.
      this.accumulatedResults.push(
        buildToolResult(
          toolUse,
          toolError(describeToolFailure(error), {}, classifyToolFailure(error)),
          0,
        ),
      );
    }
  }

  private advanceQueue(): void {
    const next = this.pendingQueue.shift();
    if (next) {
      this.currentApproval = next;
      this.callbacks.onPendingApprovalChanged(next);
    } else {
      this.currentApproval = null;
      this.callbacks.onPendingApprovalChanged(null);
      this.completeBatchIfAnswered();
    }
  }

  /**
   * Submit the batch's results once every claimed call has one, at most once
   * per batch.
   *
   * Submitting zeroes the claimed count and the results, which otherwise stay
   * until the next {@link startBatch}. Without that, a finished batch would
   * still compare as answered, and {@link detach} would submit it a second
   * time on unmount, which the server rejects because the thread has moved on.
   */
  private completeBatchIfAnswered(): void {
    if (
      this.claimedToolCount === 0 ||
      this.accumulatedResults.length !== this.claimedToolCount
    ) {
      return;
    }
    if (this.batchThreadId) {
      this.callbacks.onBatchComplete(this.batchThreadId, [
        ...this.accumulatedResults,
      ]);
    }
    this.claimedToolCount = 0;
    this.accumulatedResults = [];
  }
}
